<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import Icon from './Icon.vue';

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();

// 离线检测
const isOffline = ref(!navigator.onLine);
function updateOnlineStatus() {
  isOffline.value = !navigator.onLine;
}
onMounted(() => {
  window.addEventListener('online', updateOnlineStatus);
  window.addEventListener('offline', updateOnlineStatus);
});
onUnmounted(() => {
  window.removeEventListener('online', updateOnlineStatus);
  window.removeEventListener('offline', updateOnlineStatus);
});

// 认证页不显示导航
const isAuthPage = computed(() =>
  ['login', 'register', 'forgot-password', 'reset-password'].includes(
    route.name as string,
  ),
);

const navItems = [
  { name: 'dashboard', label: '首页', icon: 'home', path: '/dashboard' },
  { name: 'monitors', label: '监控', icon: 'bell', path: '/monitors' },
  { name: 'new-monitor', label: '新建', icon: 'plus', path: '/monitors/new' },
  { name: 'notifications', label: '通知', icon: 'mail', path: '/notifications' },
  { name: 'settings', label: '设置', icon: 'settings', path: '/settings' },
];

const desktopNavItems = computed(() => {
  const items = [...navItems];
  if (auth.isAdmin) {
    items.push({ name: 'admin', label: '管理', icon: 'shield', path: '/admin' });
  }
  return items;
});

function navigate(path: string) {
  router.push(path);
}

function logout() {
  auth.logout();
  router.push('/login');
}
</script>

<template>
  <!-- 认证页：全屏无导航 -->
  <div v-if="isAuthPage" class="min-h-screen">
    <slot />
  </div>

  <!-- 普通页面 -->
  <div v-else class="min-h-screen flex flex-col">
    <div v-if="isOffline" class="offline-banner">
      网络不可用，请检查连接
    </div>

    <!-- 顶部导航栏 -->
    <header
      class="h-14 border-b border-gray-200 dark:border-slate-800
             bg-white dark:bg-slate-900 flex items-center px-4 sticky top-0 z-10"
    >
      <div class="flex items-center gap-2 font-semibold text-gray-900 dark:text-gray-100">
        <Icon name="plane" :size="20" class="text-brand-700 dark:text-brand-400" />
        <span>假行</span>
      </div>

      <div class="ml-auto flex items-center gap-3 text-sm">
        <span v-if="auth.user" class="text-gray-500 dark:text-gray-400 hidden sm:inline text-xs">
          {{ auth.user.email }}
        </span>
        <button class="btn-ghost text-sm px-3 py-1" @click="logout">
          <Icon name="logout" :size="16" />
          <span class="ml-1">退出</span>
        </button>
      </div>
    </header>

    <div class="flex flex-1">
      <!-- 桌面端侧边栏 -->
      <aside
        class="hidden md:block w-56 border-r border-gray-200 dark:border-slate-800
               bg-white dark:bg-slate-900 p-3"
      >
        <nav class="flex flex-col gap-1">
          <button
            v-for="item in desktopNavItems"
            :key="item.name"
            class="flex items-center gap-3 px-3 py-2 rounded-md text-left text-sm
                   hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
            :class="{
              'bg-brand-50 text-brand-800 dark:bg-brand-950/40 dark:text-brand-300':
                route.path.startsWith(item.path),
              'text-gray-700 dark:text-gray-300': !route.path.startsWith(item.path),
            }"
            @click="navigate(item.path)"
          >
            <Icon :name="item.icon" :size="18" />
            <span>{{ item.label }}</span>
          </button>
        </nav>
      </aside>

      <!-- 主内容区 -->
      <main class="flex-1 overflow-auto">
        <slot />
      </main>
    </div>

    <!-- 移动端底部 tab bar -->
    <nav
      class="md:hidden fixed bottom-0 left-0 right-0 border-t
             border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900
             flex items-center justify-around z-10 h-safe-bottom"
    >
      <button
        v-for="item in navItems"
        :key="item.name"
        class="flex flex-col items-center justify-center flex-1 h-14 text-xs gap-0.5"
        :class="{
          'text-brand-700 dark:text-brand-400': route.path.startsWith(item.path),
          'text-gray-400 dark:text-gray-500': !route.path.startsWith(item.path),
        }"
        @click="navigate(item.path)"
      >
        <Icon :name="item.icon" :size="20" />
        <span>{{ item.label }}</span>
      </button>
    </nav>
  </div>
</template>
